import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Calculator, ChevronRight, Utensils, TrendingDown, Target, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const STEPS = [
  {
    icon: Calculator,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    title: 'Изчисли калориите си',
    desc: 'Попълни краткия квиз — въведи теглото, ръста и целта си. Отнема 2 минути.',
  },
  {
    icon: Utensils,
    color: 'text-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    title: 'Получи персонализиран план',
    desc: 'ИИ генерира 7-дневен хранителен план с рецепти, специално за теб.',
  },
  {
    icon: TrendingDown,
    color: 'text-sky-500',
    bg: 'bg-sky-50 dark:bg-sky-900/20',
    title: 'Следи напредъка си',
    desc: 'Записвай теглото и измеренията си всяка седмица. Виж как се промяняш.',
  },
];

export default function OnboardingModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    if (user.is_admin) return; // Admin users don't need onboarding
    const key = `onboarding_done_${user.id || user.email}`;
    if (!localStorage.getItem(key)) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [user]);

  const dismiss = () => {
    const key = `onboarding_done_${user.id || user.email}`;
    localStorage.setItem(key, '1');
    setVisible(false);
  };

  const handleStart = () => {
    dismiss();
    navigate('/calculator');
  };

  if (!visible) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="onboarding-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          data-testid="onboarding-modal"
        >
          <motion.div
            key="onboarding-card"
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 20 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 w-full max-w-md overflow-hidden"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-emerald-600 to-emerald-700 px-6 pt-8 pb-10 text-white">
              <button
                onClick={dismiss}
                data-testid="onboarding-close"
                className="absolute top-4 right-4 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="mb-3">
                <span className="text-xs font-semibold bg-white/15 px-2.5 py-1 rounded-full tracking-wide uppercase">
                  Добре дошъл в Portion
                </span>
              </div>
              <h2 className="text-2xl font-bold leading-snug mb-1">
                Здравей, {user?.full_name?.split(' ')[0] || 'приятел'}!
              </h2>
              <p className="text-emerald-100 text-sm">Ето как да започнеш за 2 минути</p>

              {/* Step dots */}
              <div className="flex gap-1.5 mt-5">
                {STEPS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setStep(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === step ? 'w-6 bg-white' : 'w-1.5 bg-white/40'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Steps */}
            <div className="px-6 pt-6 pb-2">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="flex gap-4 items-start"
                >
                  <div className={`w-11 h-11 rounded-xl ${current.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-5 h-5 ${current.color}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100 text-base mb-1">{current.title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{current.desc}</p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* All steps summary */}
            {step < STEPS.length - 1 && (
              <div className="px-6 pt-4 pb-2 space-y-2">
                {STEPS.slice(step + 1).map((s, i) => {
                  const SI = s.icon;
                  return (
                    <div key={i} className="flex items-center gap-3 opacity-40">
                      <div className={`w-7 h-7 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
                        <SI className={`w-3.5 h-3.5 ${s.color}`} />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{s.title}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Actions */}
            <div className="px-6 py-5 flex gap-3">
              {!isLast ? (
                <>
                  <button
                    onClick={dismiss}
                    className="flex-1 h-10 rounded-xl border border-gray-200 dark:border-slate-600 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    Пропусни
                  </button>
                  <button
                    onClick={() => setStep(s => s + 1)}
                    data-testid="onboarding-next"
                    className="flex-1 h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors"
                  >
                    Напред <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <button
                  onClick={handleStart}
                  data-testid="onboarding-start"
                  className="w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center justify-center gap-2 transition-colors shadow-lg shadow-emerald-500/20"
                >
                  <Calculator className="w-4 h-4" />
                  Изчисли калориите си
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
